import React, { useState, useEffect } from "react";
import Skeleton from "react-loading-skeleton"; // Import Skeleton
import { useTranslation } from "react-i18next"; // Keep for 't' function

// Define the props interface for Pagination component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean; // Add isLoading prop
}

// Define the type for viewport size
type ViewportSize = "mobile" | "tablet" | "desktop";

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false, // Use the prop directly
}) => {
  const { t } = useTranslation(); // Keep for 't' function

  // Determine the viewport size for responsive behavior
  const [viewportSize, setViewportSize] = useState<ViewportSize>("desktop");

  // Effect to update viewport size state
  useEffect(() => {
    // Function to update viewport size state
    const updateViewportSize = () => {
      if (window.innerWidth < 480) {
        setViewportSize("mobile");
      } else if (window.innerWidth < 768) {
        setViewportSize("tablet");
      } else {
        setViewportSize("desktop");
      }
    };

    // Set initial viewport size
    updateViewportSize();

    // Add resize event listener
    window.addEventListener("resize", updateViewportSize);

    // Clean up
    return () => window.removeEventListener("resize", updateViewportSize);
  }, []); // Empty dependency array means this runs once on mount and cleans up on unmount

  // Render skeleton if loading
  if (isLoading) {
    return (
      <div className="pagination-container pagination-skeleton">
        <Skeleton height={36} width={250} />
      </div>
    );
  }

  // Skip rendering if there's only one page
  if (totalPages <= 1) return null;

  // Adjust pages to show based on viewport size
  const getMaxPagesToShow = (): number => {
    switch (viewportSize) {
      case "mobile":
        return 1; // Just show current page on mobile
      case "tablet":
        return 3; // Show fewer pages on tablet
      default:
        return 5; // Full pagination on desktop
    }
  };

  const maxPagesToShow = getMaxPagesToShow();
  const pages: number[] = [];

  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  let endPage = startPage + maxPagesToShow - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const handleClick = (
    page: number,
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault();
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      // Use the provided callback
      onPageChange(page);
      window.scrollTo(0, 0);
    }
  };

  // Show simplified pagination on mobile
  if (viewportSize === "mobile") {
    return (
      <div className="pagination-container pagination-mobile">
        <ul className="pagination">
          {currentPage > 1 && (
            <li>
              <a
                href="#"
                onClick={(e) => handleClick(currentPage - 1, e)}
                className="prev-page"
                aria-label={t("pagination.prevPageLabel")} // Use translation
              >
                &laquo;
              </a>
            </li>
          )}

          <li className="current-page-indicator">
            <span>
              {t("pagination.pageIndicator", { currentPage, totalPages })}{" "}
              {/* Use translation */}
            </span>
          </li>

          {currentPage < totalPages && (
            <li>
              <a
                href="#"
                onClick={(e) => handleClick(currentPage + 1, e)}
                className="next-page"
                aria-label={t("pagination.nextPageLabel")} // Use translation
              >
                &raquo;
              </a>
            </li>
          )}
        </ul>
      </div>
    );
  }

  // For tablet and desktop, show more pagination links
  return (
    <div className={`pagination-container pagination-${viewportSize}`}>
      <ul className="pagination">
        {/* Previous button */}
        {currentPage > 1 && (
          <li>
            <a
              href="#"
              onClick={(e) => handleClick(currentPage - 1, e)}
              className="prev-page"
              aria-label={t("pagination.prevPageLabel")} // Use translation
            >
              &laquo;
              {viewportSize !== "tablet" && ` ${t("pagination.previous")}`}{" "}
              {/* Use translation */}
            </a>
          </li>
        )}

        {/* First page and ellipsis */}
        {startPage > 1 && (
          <>
            <li>
              <a href="#" onClick={(e) => handleClick(1, e)}>
                1
              </a>
            </li>
            {startPage > 2 && <li className="ellipsis">...</li>}
          </>
        )}

        {/* Page numbers */}
        {pages.map((page) => (
          <li key={page}>
            <a
              href="#"
              onClick={(e) => handleClick(page, e)}
              className={page === currentPage ? "current" : ""}
            >
              {page}
            </a>
          </li>
        ))}

        {/* Last page and ellipsis */}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <li className="ellipsis">...</li>}
            <li>
              <a href="#" onClick={(e) => handleClick(totalPages, e)}>
                {totalPages}
              </a>
            </li>
          </>
        )}

        {/* Next button */}
        {currentPage < totalPages && (
          <li>
            <a
              href="#"
              onClick={(e) => handleClick(currentPage + 1, e)}
              className="next-page"
              aria-label={t("pagination.nextPageLabel")} // Use translation
            >
              {viewportSize !== "tablet" && `${t("pagination.next")} `}{" "}
              {/* Use translation */}
              &raquo;
            </a>
          </li>
        )}
      </ul>
    </div>
  );
};

export default Pagination;
